<script lang="ts">
	import { format } from 'svelte-i18n';
	import { afterUpdate, beforeUpdate, onDestroy, onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { user_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import {
		claimEmeterPriorityDevHud,
		emeterPriorityDevForce,
		setEmeterPriorityDevHudFamily,
		type EmeterPriorityDevHudFamily,
	} from 'src/stores/features/dev/emeter-priority-dev-state';
	import { sidepanelChartMode } from 'src/stores/ui/sidepanel-public';
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import ControlOnOffAddon from 'src/components/Controls/Single/ControlOnOffAddon.svelte';
	import SidepanelPeekTray from 'src/components/Sidepanel/SidepanelPeekTray.svelte';
	import DeviceViewStateWrapper from '../DeviceViewStateWrapper.svelte';
	import EmeterPeakPowerCards from './EmeterPeakPowerCards.svelte';
	import {
		Button,
		ButtonGroupV2,
		LabeledBarList,
		PriorityGrid,
		PropertyCard,
		PropertyDock,
		Text,
		capturePropertyCardFlip,
		playPropertyCardFlip,
	} from '@Shelly/shelly-design-system';
	import {
		emeterMainMetric,
		setEmeterMainMetric,
		type EmeterMainMetric,
	} from 'src/helpers/emeter_main_metric';
	import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import { isDevModalAvailable, isNonProductionAppBuild } from 'src/helpers/dev_tools';
	import { getConfiguredEm3ChannelNames } from 'src/helpers/em3_channel_names';
	import {
		buildEmeterPriorityLayout,
		collectEmeterProtectionErrors,
		emeterPriorityFlipKey,
		extractEmeterReadings,
		type EmeterPriorityCard,
	} from 'src/helpers/emeter_priority_layout';
	import { hasEmeterCurrentReading } from 'src/helpers/emeter_sidepanel_helpers';
	import {
		emptyEmeterSparkCache,
		getEmeterSparkline,
		pushEmeterSparkSamples,
	} from 'src/helpers/emeter_spark_cache';
	import { get_mode, is_gen_2, isGen2EM } from 'src/helpers/interface_helpers';

	export let device: Device;

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore as Record<string, unknown> | undefined;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: uiStateStore = getDeviceState(device?.id);
	$: uiState = $uiStateStore;
	$: metric = $emeterMainMetric as EmeterMainMetric;
	$: channel = device?.channel ?? 0;
	$: isSingleChannel =
		isGen2EM(device?.type) ||
		device?.mode === 'monophase' ||
		device?.mode === 'power_meter' ||
		get_mode(device, status, settings) === 'monophase' ||
		Boolean(status?.[`pm1:${channel}`]);
	$: currentAvailable = hasEmeterCurrentReading(status, device, isSingleChannel);
	$: effectiveMetric =
		metric === 'current' && !currentAvailable ? ('power' as EmeterMainMetric) : metric;
	$: readings = extractEmeterReadings(status, isSingleChannel, channel);
	$: channelNames = getConfiguredEm3ChannelNames(device);
	$: errors = collectEmeterProtectionErrors(status);
	$: layout = buildEmeterPriorityLayout({
		isMonophase: isSingleChannel,
		metric: effectiveMetric,
		online: Boolean(status?.online),
		uiStateType: typeof uiState?.type === 'number' ? uiState.type : undefined,
		isLoading: Boolean(uiState?.isLoading),
		errors,
		force: $emeterPriorityDevForce,
		channelNames,
		readings,
		imbalanceEmphasis: Boolean($devices[device?.id]?.cloud_options?.imbalance_emphasis),
	});

	let sparkCache = emptyEmeterSparkCache('');
	let peekExpanded = false;
	let flipHost: HTMLElement;
	let previousFlip = { rects: new Map<string, DOMRect>(), gridHeight: 0 };
	let playedFlipKey = '';
	let flipRaf = 0;
	let releaseDevHud: (() => void) | undefined;
	let claimedHudFamily: EmeterPriorityDevHudFamily | undefined;

	$: flipKey = emeterPriorityFlipKey(layout);
	$: hudFamily = (isSingleChannel ? 'single' : 'triphase') as EmeterPriorityDevHudFamily;

	const flipRoot = (): HTMLElement | undefined => {
		const root =
			flipHost?.closest('.sidepanel-content-wrapper') instanceof HTMLElement
				? flipHost.closest('.sidepanel-content-wrapper')
				: flipHost;
		return root instanceof HTMLElement ? root : undefined;
	};

	const syncDevHud = (open: boolean, family: EmeterPriorityDevHudFamily) => {
		if (open) {
			if (!releaseDevHud) {
				releaseDevHud = claimEmeterPriorityDevHud(family);
				claimedHudFamily = family;
			} else if (claimedHudFamily !== family) {
				setEmeterPriorityDevHudFamily(family);
				claimedHudFamily = family;
			}
		} else if (releaseDevHud) {
			releaseDevHud();
			releaseDevHud = undefined;
			claimedHudFamily = undefined;
		}
	};

	$: gen2 = is_gen_2(device);
	$: peripheral = device && status ? getAddonStatus(device, [], false, true) : [];
	$: emHasSwitch = Boolean(status && 'switch:0' in status);
	$: emHasSwitchAddon = peripheral?.find((item) => item.peripheralType == 'switch');
	$: showPeekTray = gen2 && (emHasSwitch || !!emHasSwitchAddon);
	$: dockOpen = Boolean(layout.dockCard);
	$: if (dockOpen && peekExpanded) {
		peekExpanded = false;
	}
	$: switchIsOn = (() => {
		if (emHasSwitch && status?.['switch:0']) {
			return Boolean((status['switch:0'] as { output?: boolean })?.output);
		}
		if (emHasSwitchAddon?.sensor?.component) {
			return Boolean(
				(status?.[emHasSwitchAddon.sensor.component] as { output?: boolean })?.output,
			);
		}
		return false;
	})();
	$: showDevHud = isNonProductionAppBuild() && isDevModalAvailable($user_data?.email);
	$: syncDevHud(showDevHud, hudFamily);
	$: sparkCache = pushEmeterSparkSamples(sparkCache, {
		deviceId: device?.id ?? '',
		metric: effectiveMetric,
		cards: layout.cards,
		now: Date.now(),
	});

	onMount(() => {
		sidepanelChartMode.set(true);
	});

	onDestroy(() => {
		sidepanelChartMode.set(false);
		if (flipRaf) cancelAnimationFrame(flipRaf);
		flipRaf = 0;
		releaseDevHud?.();
		releaseDevHud = undefined;
		claimedHudFamily = undefined;
	});

	beforeUpdate(() => {
		const root = flipRoot();
		if (!root || flipKey === playedFlipKey) return;
		previousFlip = capturePropertyCardFlip(root);
	});

	afterUpdate(() => {
		const root = flipRoot();
		if (!root || flipKey === playedFlipKey) return;
		playedFlipKey = flipKey;
		const snapshot = previousFlip;
		previousFlip = { rects: new Map(), gridHeight: 0 };
		if (!snapshot.rects.size) return;
		if (flipRaf) cancelAnimationFrame(flipRaf);
		flipRaf = requestAnimationFrame(() => {
			flipRaf = 0;
			playPropertyCardFlip(root, snapshot);
		});
	});

	const onSelectMetric = (next: EmeterMainMetric) => {
		if (next === 'current' && !currentAvailable) return;
		setEmeterMainMetric(next);
	};

	const cardLabel = (card: EmeterPriorityCard): string => {
		if (card.labelText) return card.labelText;
		return $format(card.label.key, {
			default: card.label.default,
			values: card.label.values,
		});
	};
</script>

<DeviceViewStateWrapper
	{device}
	ignoreOfflineState
	hideSlotStaticBottom={!showPeekTray && gen2}
	hideSlotFloatingBottom={!layout.dockCard}
	staticBottomTopSpacing={showPeekTray && peekExpanded ? '0' : 'm'}
>
	<div class="emeter-priority-view" slot="content" bind:this={flipHost}>
		<ButtonGroupV2
			variant="segmented"
			aria-label={$format('label_emeter_metric_group', {
				default: 'Show power or current',
			})}
			noWrap
			fullWidth
			class="mb-m"
			styleType="body-extra-small"
		>
			<Button
				id="emeter-metric-power"
				class={effectiveMetric === 'power' ? 'active' : ''}
				size="m"
				aria-label={$format('button_emeter_metric_power', { default: 'Power' })}
				on:click={() => onSelectMetric('power')}
			>
				{$format('button_emeter_metric_power', { default: 'Power' })}
			</Button>
			<Button
				id="emeter-metric-current"
				class={effectiveMetric === 'current' ? 'active' : ''}
				size="m"
				disabled={!currentAvailable}
				aria-label={$format('button_emeter_metric_current', { default: 'Current' })}
				on:click={() => onSelectMetric('current')}
			>
				{$format('button_emeter_metric_current', { default: 'Current' })}
			</Button>
		</ButtonGroupV2>

		{#if layout.showPhaseBars && layout.phaseBars.length}
			<LabeledBarList
				class="mb-m"
				heading={$format('energy_monitoring_phase_health_title', { default: 'Phase health' })}
				disabled={Boolean(layout.overlay)}
				status={!layout.overlay && layout.phaseStatus
					? {
							label: $format(layout.phaseStatus.label.key, {
								default: layout.phaseStatus.label.default,
								values: layout.phaseStatus.label.values,
							}),
							tone: layout.phaseStatus.tone,
						}
					: undefined}
				items={layout.phaseBars}
			/>
		{/if}

		<PriorityGrid class="mb-m" animate={false}>
			{#each layout.cards as card (card.id)}
				<PropertyCard
					data-property-id={card.id}
					priority={card.priority}
					value={card.value}
					unit={card.unit}
					label={cardLabel(card)}
					icon={card.icon}
					tone={card.tone}
					stale={card.stale}
					disabled={Boolean(card.disabled || layout.overlay)}
					chart={card.priority === 'primary'
						? getEmeterSparkline(sparkCache, effectiveMetric, card, Date.now())
						: undefined}
					accentColor={card.accentColor}
					kind={card.kind}
				/>
			{/each}
		</PriorityGrid>

		<EmeterPeakPowerCards {device} />
	</div>

	<svelte:fragment slot="floatingBottom">
		<PropertyDock
			hidden={!layout.dockCard}
			label={$format('label_emeter_pinned_properties', { default: 'Pinned device properties' })}
		>
			{#if layout.dockCard}
				<PropertyCard
					data-property-id={layout.dockCard.id}
					priority="primary"
					value={layout.dockCard.value}
					unit={layout.dockCard.unit}
					label={cardLabel(layout.dockCard)}
					icon={layout.dockCard.icon}
					tone={layout.dockCard.tone}
					accentColor={layout.dockCard.accentColor}
					kind={layout.dockCard.kind ?? 'status'}
				/>
			{/if}
		</PropertyDock>
	</svelte:fragment>

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		{#if showPeekTray}
			<SidepanelPeekTray bind:expanded={peekExpanded} disabled={dockOpen}>
				<svelte:fragment slot="peek">
					<Text styleType="miniature" class="text-color-muted">
						{switchIsOn
							? $format('label_peek_tray_on', { default: 'On' })
							: $format('label_peek_tray_off', { default: 'Off' })}
					</Text>
				</svelte:fragment>

				{#if emHasSwitchAddon && emHasSwitch}
					<div class="d-flex gap-s">
						<div class="control-flex-column flex-1">
							<ControlOnOffAddon
								{device}
								{status}
								isInSlot
								bare
								useV2Components={true}
								component={emHasSwitchAddon?.sensor?.component}
							/>
						</div>
						<div class="control-flex-column flex-1">
							<ControlOnOff
								{device}
								{status}
								{settings}
								isInSlot
								bare
								useV2Components={true}
							/>
						</div>
					</div>
				{:else if emHasSwitch && !emHasSwitchAddon}
					<ControlOnOff {device} {status} {settings} isWide bare useV2Components={true} />
				{:else if emHasSwitchAddon}
					<ControlOnOffAddon
						{device}
						{status}
						isWide
						bare
						useV2Components={true}
						component={emHasSwitchAddon?.sensor?.component}
					/>
				{/if}
			</SidepanelPeekTray>
		{/if}
	</div>
</DeviceViewStateWrapper>
